import React from "react";
import { useMessageActions } from "../hooks/useMessageActions";
import { useMessages } from "../providers/MessagesContext";
import { processCustomTags } from "../utils/textUtils";
import { CONGRATS_MESSAGE_UUID } from "../utils/constants";

export interface OptionButtonsProps {
  options: Array<[string, string]>;
  parentNodeUuid: string;
  isReadOnly?: boolean;
  elementId: string;
  isDefinitionNode?: boolean;
}

const OptionButtons: React.FC<OptionButtonsProps> = ({
  options,
  parentNodeUuid,
  isReadOnly = false,
  elementId,
  isDefinitionNode = false,
}) => {
  const { onOptionClick } = useMessageActions();
  const { messages } = useMessages();

  // Filter out the "I am still experiencing this problem" option
  const filteredOptions = options.filter((option) => {
    const [optionText] = option;
    return !(
      optionText.includes("I am still experiencing this problem") ||
      optionText.includes("[Next]")
    );
  });

  // Capture a snapshot of messages on mount to avoid re-renders when messages change
  const messagesSnapshotRef = React.useRef<typeof messages | null>(null);

  // Only capture the snapshot once on mount
  if (messagesSnapshotRef.current === null) {
    messagesSnapshotRef.current = messages;
  }

  // Calculate viewed status based on the initial snapshot, not current messages
  const viewedStatusMap = React.useMemo(() => {
    const statusMap = new Map<string, boolean>();
    const snapshotMessages = messagesSnapshotRef.current || [];

    filteredOptions.forEach((option) => {
      const [, nodeUuid] = option;
      if (nodeUuid === CONGRATS_MESSAGE_UUID) {
        statusMap.set(nodeUuid, false);
      } else {
        const isViewed = snapshotMessages.some(
          (message) => message.nodeUuid === nodeUuid
        );
        statusMap.set(nodeUuid, isViewed);
      }
    });
    return statusMap;
  }, [filteredOptions]); // Only depend on filteredOptions, not messages

  return (
    <div className="mt-2 sm:mt-3 flex flex-col space-y-1.5 sm:space-y-2">
      {filteredOptions.map((option, filteredIndex) => {
        const [optionText, nodeUuid] = option;
        const hasOptionBeenViewed = viewedStatusMap.get(nodeUuid) || false;

        return (
          <button
            key={`option-${filteredIndex}`}
            onClick={
              isReadOnly
                ? undefined
                : () => {
                    onOptionClick(
                      nodeUuid,
                      optionText,
                      parentNodeUuid,
                      elementId,
                      isDefinitionNode
                    );
                  }
            }
            data-trust-trigger="option-buttons"
            className={`block text-center px-3 sm:px-4 py-2 sm:py-3 rounded-full transition-colors duration-200 text-xs sm:text-sm font-medium shadow-md max-w-full text-white hover:shadow-lg ${
              hasOptionBeenViewed
                ? "bg-[#e0a596] hover:bg-[#E38C76]"
                : "bg-[#E38C76] hover:bg-[#d17a64]"
            }`}
          >
            {processCustomTags(optionText) +
              (hasOptionBeenViewed ? " (Viewed Already)" : "")}
          </button>
        );
      })}
    </div>
  );
};

export default OptionButtons;
